import React, { useState } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormLabelProps,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdOutlinePhotoLibrary } from 'react-icons/md';
import { Field, Form, Formik, FormikProps } from 'formik';
import { useMutation, useQueryClient } from 'react-query';

import { ServiceType } from '../types';
import { DEBUG_FORM } from '../constants';
import { MyEditableModal } from './MyEditableModal';
import { MySubmitButton } from './MyCustomComponents';
import { useServiceQuery } from '../hooks/useServiceQuery';
import { CategoryAutoComplete } from './CategoryAutoComplete';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { createService, updateService } from '../api/services';
import { setSelectedCategory } from '../redux/slices/selectedCategorySlice';
import { clearEditingServiceId } from '../redux/slices/ServiceEditorSlice';
import styles from './styles/ServiceEditor.module.scss';

const RECURRENCE_ITEMS = ['weekly', 'monthly', 'yearly'];

interface MyFormLabelProps {
  isFirstItem?: boolean;
}

const MyFormLabel: React.FC<FormLabelProps & MyFormLabelProps> = ({
  isFirstItem = false,
  ...props
}) => {
  return (
    <FormLabel
      mt={isFirstItem ? '0' : '15px'}
      className={styles.inputLabel}
      {...props}
    >
      {props.children}
    </FormLabel>
  );
};

// const MyOptional: React.FC = ({ children }) => (
//   <div style={{ color: '#ccc' }}>{children}</div>
// );

export const ServiceEditor: React.FC = () => {
  // i18n
  const { t } = useTranslation();

  const navigate = useNavigate();

  // UI states
  const editingServiceId = useAppSelector(
    (state) => state.serviceEditor.editingServiceId
  );
  const hasCookies = useAppSelector((state) => state.auth.hasCookies);
  const dispatch = useAppDispatch();

  // server states
  const queryClient = useQueryClient();
  const { data: serviceData } = useServiceQuery(hasCookies);
  // create service
  const { mutate: createMutate, isLoading: isCreateLoading } = useMutation(
    createService,
    {
      onSuccess: (updatedData) => {
        handleMutationSuccess(updatedData!);
      },
    }
  );
  // update service
  const { mutate: updateMutate, isLoading: isUpdateLoading } = useMutation(
    updateService,
    {
      onSuccess: (updatedData) => {
        handleMutationSuccess(updatedData!);
      },
    }
  );

  // for modal
  const { isOpen, onClose, onOpen } = useDisclosure();

  // for logo image
  const [isLogoImgLoaded, setIsLogoImgLoaded] = useState(false);

  const handleMutationSuccess = (updatedData: ServiceType[]) => {
    console.log('successful update mutation');
    queryClient.setQueriesData('services', updatedData);
    dispatch(setSelectedCategory('All Services'));
    dispatch(clearEditingServiceId());
    navigate('/');
  };

  return (
    <>
      <Formik
        initialValues={
          editingServiceId === ''
            ? {
                serviceUrl: '',
                imgUrl: '',
                serviceName: '',
                category: '',
                price: 0,
                recurrence: '',
              }
            : serviceData!.filter(
                (service) => service.id === editingServiceId
              )[0]
        }
        onSubmit={(values) => {
          console.log(values);
        }}
      >
        {({ values, setFieldValue }: FormikProps<ServiceType>) => (
          <>
            <div className={styles.container}>
              <Form>
                {/* service URL */}
                <Field name="serviceUrl">
                  {({ field, form }: any) => (
                    <FormControl
                      isInvalid={form.errors.name && form.touched.name}
                    >
                      <Flex justifyContent={'space-between'}>
                        <MyFormLabel isFirstItem={true} htmlFor="serviceUrl">
                          {t('url')}
                        </MyFormLabel>
                        {/* <MyOptional>Optional</MyOptional> */}
                      </Flex>

                      <Input
                        {...field}
                        id="serviceUrl"
                        variant="myinput"
                        placeholder="https://www.netflix.com"
                      />
                      <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* Image URL */}
                <Field name="imgUrl">
                  {({ field, form }: any) => (
                    <FormControl
                      isInvalid={form.errors.name && form.touched.name}
                    >
                      <Flex justifyContent={'space-between'}>
                        <MyFormLabel htmlFor="imgUrl">{t('logo')}</MyFormLabel>
                        {/* <MyOptional>Optional</MyOptional> */}
                      </Flex>

                      <div className={styles.imgUrl}>
                        <div>
                          <img
                            style={{
                              display: isLogoImgLoaded ? 'inline' : 'none',
                              border: 'solid 1px #eee',
                            }}
                            src={values.imgUrl}
                            alt="logo"
                            onError={() => setIsLogoImgLoaded(false)}
                            onLoad={() => setIsLogoImgLoaded(true)}
                          />

                          <MdOutlinePhotoLibrary
                            style={{
                              display:
                                values.imgUrl === '' || !isLogoImgLoaded
                                  ? 'block'
                                  : 'none',
                              width: '43px',
                              height: '43px',
                              border: 'solid 1px #eee',
                              padding: '6px',
                            }}
                          />
                        </div>

                        <Input
                          {...field}
                          id="imgUrl"
                          variant="myinput"
                          placeholder={t('imgUrl-placeholder')}
                          className={styles.imgUrl__input}
                        />
                      </div>

                      <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* service name */}
                <Field name="serviceName">
                  {({ field, form }: any) => (
                    <FormControl
                      isInvalid={form.errors.name && form.touched.name}
                    >
                      <MyFormLabel htmlFor="serviceName">
                        {t('name')}
                      </MyFormLabel>

                      <Input
                        {...field}
                        id="serviceName"
                        variant="myinput"
                        placeholder="Netflix"
                      />
                      <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* service category */}
                <Field name="category">
                  {({ field, form }: any) => (
                    <FormControl
                      isInvalid={form.errors.name && form.touched.name}
                    >
                      <MyFormLabel htmlFor="category">
                        {t('category')}
                      </MyFormLabel>

                      <CategoryAutoComplete
                        // for formik field
                        setFieldValue={setFieldValue}
                        id="category"
                        variant="myinput"
                        placeholder={t('category-placeholder')}
                        onOpenModal={onOpen}
                        categoryInputValue={values.category}
                        {...field}
                      />

                      <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* service price */}
                <Field name="price">
                  {({ field, form }: any) => (
                    <FormControl
                      isInvalid={form.errors.name && form.touched.name}
                    >
                      <MyFormLabel htmlFor="price">{t('price')}</MyFormLabel>

                      <InputGroup variant="myinput">
                        <InputLeftElement
                          pointerEvents="none"
                          color="#ccc"
                          children="$"
                          fontSize="19px"
                          paddingTop="2px"
                          zIndex={'1'}
                        />
                        <Input
                          {...field}
                          id="price"
                          value={values.price === 0 ? '' : values.price}
                          variant="myinput"
                          placeholder="0 - 1,000,000"
                          type={'number'}
                        />
                      </InputGroup>
                      <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* service recurrence */}
                <Field name="recurrence">
                  {({ form }: any) => (
                    <FormControl
                      isInvalid={form.errors.name && form.touched.name}
                    >
                      <MyFormLabel htmlFor="recurrence">
                        {t('recurrence')}
                      </MyFormLabel>

                      <Menu matchWidth={true}>
                        <MenuButton
                          // variant="menuBtn"
                          className={styles.menubtn}
                          color={values.recurrence ? '#000' : '#ccc'}
                          as={Button}
                          rightIcon={
                            <ChevronDownIcon className={styles.downicon} />
                          }
                        >
                          {values.recurrence === ''
                            ? t('recurrence-placeholder')
                            : values.recurrence}
                        </MenuButton>
                        <MenuList className={styles.recurrenceList}>
                          {RECURRENCE_ITEMS.map((item, index) => (
                            <MenuItem
                              className={styles.recurrenceItem}
                              key={index}
                              onClick={() =>
                                setFieldValue('recurrence', item.toLowerCase())
                              }
                            >
                              {t(item)}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    </FormControl>
                  )}
                </Field>

                <MySubmitButton
                  type="submit"
                  mt="23px"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    {
                      editingServiceId === ''
                        ? createMutate(values)
                        : updateMutate(values);
                    }
                  }}
                  isLoading={
                    editingServiceId === '' ? isCreateLoading : isUpdateLoading
                  }
                >
                  {editingServiceId === '' ? t('new') : t('save')}
                </MySubmitButton>

                {DEBUG_FORM && (
                  <pre style={{ marginTop: '20px', fontSize: '14px' }}>
                    {JSON.stringify(values, null, 2)}
                  </pre>
                )}
              </Form>
            </div>

            <MyEditableModal
              isOpen={isOpen}
              onClose={onClose}
              headerText={t('edit-category-modal-header')}
              submitBtnText={t('save')}
              cancelBtnText={t('cancel')}
              setFieldValue={setFieldValue}
              categoryInputValue={values.category}
            />
          </>
        )}
      </Formik>
    </>
  );
};
